import { useState, useRef, useEffect, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { analytics } from '@/lib/analytics';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

// Handle inline formatting like **bold**
const formatInlineMarkdown = (text: string): ReactNode[] => {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) => 
    i % 2 === 1 ? <strong key={i} className="font-semibold">{part}</strong> : part
  );
};

// Helper function to render formatted text with markdown support
const renderFormattedMessage = (content: string): ReactNode => {
  // Pre-process: Convert inline bullets to proper line breaks
  let normalizedContent = content
    // Add newline before inline bullets (* Item or - Item) when preceded by text
    .replace(/([.!?:;])\s*\*\s+\*\*/g, '$1\n* **')
    .replace(/([.!?:;])\s*-\s+\*\*/g, '$1\n- **')
    .replace(/([.!?:;])\s*\*\s+([A-Z])/g, '$1\n* $2')
    .replace(/([.!?:;])\s*-\s+([A-Z])/g, '$1\n- $2')
    // Add newline before inline numbered lists (1. Item, 2. Item)
    .replace(/([.!?:;])\s+(\d+)\.\s+\*\*/g, '$1\n$2. **')
    .replace(/([.!?:;])\s+(\d+)\.\s+([A-Z])/g, '$1\n$2. $3')
    // Normalize multiple newlines
    .replace(/\n{3,}/g, '\n\n');
  
  const lines = normalizedContent.split('\n');
  
  return (
    <div className="space-y-1">
      {lines.map((line, index) => {
        // Handle bullet points (* or -)
        if (line.match(/^[\*\-]\s/)) {
          const bulletContent = line.replace(/^[\*\-]\s/, '');
          return (
            <div key={index} className="flex gap-2 ml-1">
              <span className="text-primary">•</span>
              <span>{formatInlineMarkdown(bulletContent)}</span>
            </div>
          );
        }
        
        // Handle numbered lists
        if (line.match(/^\d+\.\s/)) {
          const match = line.match(/^(\d+)\.\s(.*)$/);
          if (match) {
            return (
              <div key={index} className="flex gap-2 ml-1">
                <span className="text-primary min-w-[1.25rem]">{match[1]}.</span>
                <span>{formatInlineMarkdown(match[2])}</span>
              </div>
            );
          }
        }
        
        // Empty line creates spacing
        if (line.trim() === '') {
          return <div key={index} className="h-2" />;
        }
        
        // Regular text
        return <div key={index}>{formatInlineMarkdown(line)}</div>;
      })}
    </div>
  );
};

export function ChatJCI() {
const { t } = useTranslation();
  const { user, session, profile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Add greeting message when chat opens for the first time
  useEffect(() => {
    if (isOpen && messages.length === 0 && user) {
      const userName = (profile as any)?.full_name?.split(' ')[0] || t('chatJCI.defaultName');
      setMessages([{
        role: 'assistant',
        content: t('chatJCI.greeting', { name: userName })
      }]);
    }
  }, [isOpen, t, messages.length, user, profile]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    // Track message sent
    analytics.chatInteraction('send_message');

    const userMessage: Message = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Get the current session token
      const accessToken = session?.access_token;
      if (!accessToken) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: t('chatJCI.authRequired')
        }]);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-jci`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            messages: [...messages, userMessage].map(m => ({
              role: m.role,
              content: m.content
            }))
          }),
        }
      );

      if (response.status === 401) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: t('chatJCI.authRequired')
        }]);
        return;
      }

      if (response.status === 429) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: t('chatJCI.rateLimitMessage')
        }]);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      // Track response received
      analytics.chatInteraction('receive_response');
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response || t('chatJCI.fallbackMessage')
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: t('chatJCI.errorMessage')
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed bottom-6 right-6 z-50"
          >
            <Button
              onClick={() => {
                analytics.chatInteraction('open');
                setIsOpen(true);
              }}
              size="lg"
              className="h-14 w-14 rounded-full shadow-lg bg-primary hover:bg-primary/90"
              aria-label={t('chatJCI.openChat')}
            >
              <MessageCircle className="h-6 w-6" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] max-w-[400px] h-[500px] max-h-[calc(100vh-6rem)] bg-background border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                <span className="font-semibold">{t('chatJCI.title')}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  analytics.chatInteraction('close');
                  setIsOpen(false);
                }}
                className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
                aria-label={t('chatJCI.closeChat')}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex",
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[85%] rounded-lg px-4 py-3 text-sm",
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    )}
                  >
                    {message.role === 'assistant' 
                      ? renderFormattedMessage(message.content)
                      : message.content
                    }
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-lg px-4 py-3 max-w-[85%]">
                    <div className="flex items-center gap-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce [animation-delay:-0.3s]" />
                        <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce [animation-delay:-0.15s]" />
                        <div className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {t('chatJCI.thinking')}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-border">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('chatJCI.placeholder')}
                  disabled={isLoading}
                  className="flex-1 px-3 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                />
                <Button
                  onClick={sendMessage}
                  disabled={!input.trim() || isLoading}
                  size="icon"
                  className="h-10 w-10"
                  aria-label={t('chatJCI.send')}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
